import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	Anchor,
	Button,
	Group,
	Loader,
	Paper,
	Select,
	Stack,
	Table,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
	discountKeys,
	grantDiscount,
	listAdminDiscounts,
	listDiscountGrants,
	revokeDiscountGrant,
} from "@/features/discounts/api/api";
import {
	type GrantFormValues,
	grantSchema,
} from "@/features/discounts/lib/grant-schema";
import { showDiscountError } from "@/features/discounts/lib/use-discount-error";
import { guestUserKeys } from "@/features/users/api/admin-users";
import { CardPreviewPanel } from "@/features/users/ui/card-preview-panel";
import { GuestUserPicker } from "@/features/users/ui/guest-user-picker";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { parseIsoDate, toIsoDate } from "@/shared/lib/iso-date";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

export function GrantsTab() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [discountId, setDiscountId] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [previewUserId, setPreviewUserId] = useState<string | null>(null);
	const [pendingRevoke, setPendingRevoke] = useState<{
		userId: string;
		name: string;
	} | null>(null);

	const {
		control,
		handleSubmit,
		reset,
		formState: { isSubmitting },
	} = useForm<GrantFormValues>({
		resolver: zodResolver(grantSchema),
		defaultValues: { userId: "", categoryId: "", expiresAt: "" },
	});

	const offersQuery = useQuery({
		queryKey: discountKeys.list({ limit: 100 }),
		queryFn: () => listAdminDiscounts({ limit: 100 }),
	});

	const selected = offersQuery.data?.data.find((row) => row.id === discountId);

	const grantsQuery = useQuery({
		queryKey: discountKeys.grants(discountId ?? "", page),
		queryFn: () =>
			listDiscountGrants(discountId as string, { page, limit: 20 }),
		enabled: Boolean(discountId),
	});

	const grantMutation = useMutation({
		mutationFn: (values: GrantFormValues) =>
			grantDiscount(discountId as string, {
				userId: values.userId.trim(),
				categoryId: values.categoryId,
				expiresAt: values.expiresAt.trim() || undefined,
			}),
		onSuccess: async (_grant, values) => {
			const userId = values.userId.trim();
			notifications.show({
				color: "green",
				message: t("discounts.grantCreated"),
			});
			setPreviewUserId(userId);
			reset({ userId: "", categoryId: "", expiresAt: "" });
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: discountKeys.all }),
				queryClient.invalidateQueries({
					queryKey: guestUserKeys.card(userId),
				}),
			]);
		},
		onError: showDiscountError,
	});

	const revokeMutation = useMutation({
		mutationFn: (targetUserId: string) =>
			revokeDiscountGrant(discountId as string, targetUserId),
		onSuccess: async () => {
			setPendingRevoke(null);
			notifications.show({
				color: "orange",
				message: t("discounts.grantRevoked"),
			});
			await queryClient.invalidateQueries({ queryKey: discountKeys.all });
		},
		onError: showDiscountError,
	});

	return (
		<Stack gap="md">
			<Paper p="md" radius="lg" withBorder>
				<Select
					mb="md"
					label={t("discounts.offer")}
					data={(offersQuery.data?.data ?? [])
						.filter((row) => row.type !== "EVERYONE")
						.map((row) => ({
							value: row.id,
							label: `${row.name} (${row.type})`,
						}))}
					value={discountId}
					onChange={(value) => {
						setDiscountId(value);
						setPage(1);
						reset({ userId: "", categoryId: "", expiresAt: "" });
					}}
				/>
				{offersQuery.error ? (
					<Alert mb="md" color="red" title={t("common.loadError")}>
						{describeApiErrorSync(offersQuery.error)}
					</Alert>
				) : null}
				{selected?.type === "CORPORATE" ? (
					<Alert mb="md">{t("discounts.corporateNoGrant")}</Alert>
				) : null}
				{selected?.type === "FAMILY" ? (
					<Alert mb="md" color="orange">
						{t("discounts.familyNoCard")}
					</Alert>
				) : null}
				{selected && selected.type !== "CORPORATE" ? (
					<form
						onSubmit={handleSubmit((values) => grantMutation.mutate(values))}
					>
						<Stack gap="sm" mb="md">
							<Controller
								name="userId"
								control={control}
								render={({ field, fieldState }) => (
									<GuestUserPicker
										value={field.value}
										onChange={field.onChange}
										label={t("discounts.userId")}
										description={t("discounts.userIdHint")}
										error={fieldState.error?.message}
									/>
								)}
							/>
							<Group align="flex-end" wrap="wrap">
								<Controller
									name="categoryId"
									control={control}
									render={({ field, fieldState }) => (
										<Select
											label={t("discounts.category")}
											data={selected.categories.map((category) => ({
												value: category.id,
												label: category.code
													? `${category.name} (${category.code})`
													: category.name,
											}))}
											value={field.value || null}
											onChange={(value) => field.onChange(value ?? "")}
											error={fieldState.error?.message}
											style={{ flex: 1, minWidth: 220 }}
										/>
									)}
								/>
								<Controller
									name="expiresAt"
									control={control}
									render={({ field }) => (
										<DatePickerInput
											label={t("verification.expiresAt")}
											placeholder={t("discounts.expiresPlaceholder")}
											valueFormat="DD.MM.YYYY"
											clearable
											value={parseIsoDate(field.value)}
											onChange={(date) => {
												if (!date) {
													field.onChange("");
													return;
												}
												field.onChange(toIsoDate(date));
											}}
											style={{ flex: 1, minWidth: 180 }}
										/>
									)}
								/>
								<Button
									type="submit"
									loading={grantMutation.isPending || isSubmitting}
								>
									{t("discounts.grant")}
								</Button>
							</Group>
						</Stack>
					</form>
				) : null}
				{grantsQuery.error ? (
					<Alert mb="md" color="red" title={t("common.loadError")}>
						{describeApiErrorSync(grantsQuery.error)}
					</Alert>
				) : null}
				{grantsQuery.isLoading ? (
					<Loader />
				) : (
					<Table.ScrollContainer type="native" minWidth={720}>
						<Table verticalSpacing="sm">
							<Table.Thead>
								<Table.Tr>
									<Table.Th>{t("discounts.user")}</Table.Th>
									<Table.Th>{t("common.phone")}</Table.Th>
									<Table.Th>{t("discounts.category")}</Table.Th>
									<Table.Th>{t("verification.expiresAt")}</Table.Th>
									<Table.Th />
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{grantsQuery.data?.data.map((row) => (
									<Table.Tr key={row.id}>
										<Table.Td>{row.name ?? row.userId}</Table.Td>
										<Table.Td>{row.phone}</Table.Td>
										<Table.Td>{row.categoryCode}</Table.Td>
										<Table.Td>
											{row.expiresAt
												? new Date(row.expiresAt).toLocaleDateString("uk-UA")
												: t("common.dash")}
										</Table.Td>
										<Table.Td>
											<Group gap="xs" justify="flex-end">
												<Button
													size="xs"
													variant="light"
													onClick={() => setPreviewUserId(row.userId)}
												>
													{t("discounts.showCard")}
												</Button>
												<Anchor
													component={Link}
													to={`/users/${row.userId}`}
													size="sm"
												>
													{t("users.openUser")}
												</Anchor>
												<Button
													size="xs"
													color="red"
													variant="light"
													onClick={() =>
														setPendingRevoke({
															userId: row.userId,
															name: row.name ?? row.phone ?? row.userId,
														})
													}
												>
													{t("discounts.revoke")}
												</Button>
											</Group>
										</Table.Td>
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
					</Table.ScrollContainer>
				)}
			</Paper>
			{previewUserId ? <CardPreviewPanel userId={previewUserId} /> : null}
			<ConfirmDialog
				opened={Boolean(pendingRevoke)}
				title={t("discounts.confirmRevokeTitle")}
				body={t("discounts.confirmRevokeBody", {
					name: pendingRevoke?.name ?? "",
				})}
				confirmLabel={t("discounts.confirmRevokeAction")}
				loading={revokeMutation.isPending}
				onCancel={() => setPendingRevoke(null)}
				onConfirm={() => {
					if (!pendingRevoke) {
						return;
					}
					revokeMutation.mutate(pendingRevoke.userId);
				}}
			/>
		</Stack>
	);
}
